const MINIMUM_TOTAL_RISK_COST = 'MINIMUM_TOTAL_RISK_COST'
const MINIMUM_RESIDUAL_RISK = 'MINIMUM_RESIDUAL_RISK'

const CHECK_DEFINITIONS = [
    {
        name: 'totalRiskCost',
        threshold: 'maxTotalRiskCost',
        missingReason: 'TOTAL_RISK_COST_DATA_MISSING',
        exceededReason: 'TOTAL_RISK_COST_LIMIT_EXCEEDED'
    },
    {
        name: 'residualExpectedLoss',
        threshold: 'maxResidualExpectedLoss',
        missingReason: 'RESIDUAL_RISK_DATA_MISSING',
        exceededReason: 'RESIDUAL_RISK_LIMIT_EXCEEDED'
    },
    {
        name: 'capitalAtRiskP95',
        threshold: 'maxCapitalAtRiskP95',
        missingReason: 'CAPITAL_AT_RISK_DATA_MISSING',
        exceededReason: 'CAPITAL_AT_RISK_LIMIT_EXCEEDED'
    }
]

function isUsableObject(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false

    const prototype = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
}

function finiteNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value !== 'string' || value.trim() === '') return null

    const normalized = Number(value)
    return Number.isFinite(normalized) ? normalized : null
}

function normalizeThreshold(value) {
    const normalized = finiteNumber(value)
    return normalized === null ? null : Math.max(0, normalized)
}

function alternativeId(candidate) {
    if (candidate === null || typeof candidate !== 'object') return null

    const { id } = candidate
    if (typeof id === 'string' && id.trim() !== '') return id
    if (typeof id === 'number' && Number.isFinite(id)) return id
    return null
}

function actualValue(candidate, name) {
    return candidate !== null && typeof candidate === 'object' ? candidate[name] : undefined
}

function evaluateCheck(value, threshold, missingReason, exceededReason) {
    if (threshold === null) {
        return { configured: false, actualValue: value, threshold: null, passed: true, reason: null }
    }

    const normalized = finiteNumber(value)
    if (normalized === null) {
        return { configured: true, actualValue: value, threshold, passed: false, reason: missingReason }
    }
    if (normalized > threshold) {
        return { configured: true, actualValue: value, threshold, passed: false, reason: exceededReason }
    }
    return { configured: true, actualValue: value, threshold, passed: true, reason: null }
}

function evaluateCandidate(candidate, thresholds) {
    const checks = {}
    const failureReasons = []

    CHECK_DEFINITIONS.forEach((definition) => {
        const check = evaluateCheck(
            actualValue(candidate, definition.name),
            thresholds[definition.threshold],
            definition.missingReason,
            definition.exceededReason
        )
        checks[definition.name] = check
        if (!check.passed) failureReasons.push(check.reason)
    })

    return {
        alternativeId: alternativeId(candidate),
        isCompliant: failureReasons.length === 0,
        checks,
        failureReasons
    }
}

function selectCandidate(candidates, property) {
    let selected = null
    let selectedValue = null

    candidates.forEach((candidate) => {
        const value = finiteNumber(actualValue(candidate, property))
        if (value !== null && (selected === null || value < selectedValue)) {
            selected = candidate
            selectedValue = value
        }
    })
    return selected
}

export function applyDecisionPolicy(decisionSet = {}, policy = {}) {
    const source = isUsableObject(decisionSet) ? decisionSet : {}
    const candidates = Array.isArray(source.candidates) ? source.candidates : []
    const policySource = isUsableObject(policy) ? policy : {}
    const selectionCriterion = policySource.selectionCriterion == null ? null : policySource.selectionCriterion
    const normalizedPolicy = {
        maxTotalRiskCost: normalizeThreshold(policySource.maxTotalRiskCost),
        maxResidualExpectedLoss: normalizeThreshold(policySource.maxResidualExpectedLoss),
        maxCapitalAtRiskP95: normalizeThreshold(policySource.maxCapitalAtRiskP95),
        selectionCriterion
    }
    const evaluations = candidates.map((candidate) => evaluateCandidate(candidate, normalizedPolicy))
    const compliantCandidates = candidates.filter((candidate, index) => evaluations[index].isCompliant)
    const nonCompliantCandidates = candidates.filter((candidate, index) => !evaluations[index].isCompliant)
    const criterionPresent = selectionCriterion !== null
    const criterionValid = selectionCriterion === MINIMUM_TOTAL_RISK_COST || selectionCriterion === MINIMUM_RESIDUAL_RISK
    const thresholdsConfigured = CHECK_DEFINITIONS.some(
        ({ threshold }) => normalizedPolicy[threshold] !== null
    )

    let selectedAlternative = null
    let status
    let selectionReason = null

    if (candidates.length === 0) {
        status = 'NO_CANDIDATES'
    } else if (criterionPresent && !criterionValid) {
        status = 'INVALID_SELECTION_CRITERION'
    } else if (!thresholdsConfigured && !criterionPresent) {
        status = 'POLICY_NOT_CONFIGURED'
    } else if (criterionValid) {
        const property = selectionCriterion === MINIMUM_TOTAL_RISK_COST ? 'totalRiskCost' : 'residualExpectedLoss'
        selectedAlternative = selectCandidate(compliantCandidates, property)
        if (selectedAlternative === null) {
            status = 'NO_COMPLIANT_CANDIDATE'
        } else {
            status = 'SELECTED'
            selectionReason =
                selectionCriterion === MINIMUM_TOTAL_RISK_COST
                    ? 'LOWEST_TOTAL_RISK_COST'
                    : 'LOWEST_RESIDUAL_EXPECTED_LOSS'
        }
    } else {
        status = 'NO_COMPLIANT_CANDIDATE'
    }

    return {
        selectedAlternative,
        compliantCandidates,
        nonCompliantCandidates,
        evaluations,
        policy: normalizedPolicy,
        decision: { status, selectionCriterion, selectionReason },
        metadata: {
            evaluatedCandidates: candidates.length,
            compliantCandidates: compliantCandidates.length,
            nonCompliantCandidates: nonCompliantCandidates.length
        }
    }
}
